import React, { useEffect, useState } from "react";
import { auth, db } from "./firebase";
import { ref, get, push, set } from "firebase/database";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { FaUser, FaEnvelope, FaPhone, FaUserShield, FaBook } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const CompleteRegistration = () => {
    const navigate = useNavigate();

    const [email, setEmail] = useState("");
    const [formData, setFormData] = useState({
        name: "",
        phone: "",
        parentPhone: "",
        course: ""
    });
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");
    const [hasExistingRegistration, setHasExistingRegistration] = useState(false);
    const [existingRegistrationData, setExistingRegistrationData] = useState(null);

    useEffect(() => {
        const currentUser = auth.currentUser;
        if (!currentUser) {
            navigate("/login", { replace: true });
            return;
        }
        setEmail(currentUser.email || "");

        // Load courses
        const loadCourses = async () => {
            try {
                const coursesRef = ref(db, "courses");
                const snapshot = await get(coursesRef);
                if (snapshot.exists()) {
                    const coursesData = snapshot.val();
                    const courseList = Object.keys(coursesData).map((courseId) => ({
                        id: courseId,
                        name: coursesData[courseId].name || courseId,
                    }));
                    setCourses(courseList);
                }
            } catch (e) {
                setError("Failed to load courses. Please try again.");
            }
        };

        // Check if user already has any registration request
        const checkExistingRegistration = async () => {
            try {
                const emailKey = normalizeEmail(currentUser.email || "");

                // Check in students collection
                const studentRef = ref(db, `students/${emailKey}`);
                const studentSnapshot = await get(studentRef);

                if (studentSnapshot.exists()) {
                    const studentData = studentSnapshot.val();
                    if (studentData.status === "pending") {
                        setHasExistingRegistration(true);
                        setExistingRegistrationData(studentData);
                        return; // Exit early if pending registration exists
                    }
                }

                // Check in registrationRequests collection for any existing requests
                const registrationRequestsRef = ref(db, "registrationRequests");
                const requestsSnapshot = await get(registrationRequestsRef);

                if (requestsSnapshot.exists()) {
                    const requestsData = requestsSnapshot.val();
                    const existingRequest = Object.values(requestsData).find(
                        request => request.email === currentUser.email
                    );

                    if (existingRequest) {
                        setHasExistingRegistration(true);
                        setExistingRegistrationData(existingRequest);
                    }
                }
            } catch (e) {
                console.error("Error checking existing registration:", e);
            }
        };

        loadCourses();
        checkExistingRegistration();
    }, [navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    function normalizeEmail(email) {
        return (email || "").trim().toLowerCase().replace(/\./g, ',');
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setMessage("");

        if (!formData.name || !email || !formData.phone || !formData.parentPhone || !formData.course) {
            setError("Please fill in all fields.");
            return;
        }

        // Double-check that no registration already exists
        if (hasExistingRegistration) {
            setError("You already have a registration request. Please wait for admin approval.");
            return;
        }

        setLoading(true);
        try {
            // Final check: verify no registration exists in database
            const emailKeyForCheck = normalizeEmail(email);
            const studentRef = ref(db, `students/${emailKeyForCheck}`);
            const studentSnapshot = await get(studentRef);

            if (studentSnapshot.exists()) {
                setError("A registration already exists for this email. Please wait for admin approval.");
                setLoading(false);
                return;
            }

            // Check registrationRequests collection as well
            const registrationRequestsRef = ref(db, "registrationRequests");
            const requestsSnapshot = await get(registrationRequestsRef);

            if (requestsSnapshot.exists()) {
                const requestsData = requestsSnapshot.val();
                const existingRequest = Object.values(requestsData).find(
                    request => request.email === email
                );

                if (existingRequest) {
                    setError("A registration request already exists for this email. Please wait for admin approval.");
                    setLoading(false);
                    return;
                }
            }

            // Create registration request
            const newRegistrationRef = push(registrationRequestsRef);
            const registrationData = {
                name: formData.name,
                email: email,
                phone: formData.phone,
                parentPhone: formData.parentPhone,
                course: formData.course,
                status: "pending",
                userId: auth.currentUser ? auth.currentUser.uid : null,
                createdAt: new Date().toISOString(),
            };
            await set(newRegistrationRef, registrationData);

            // Add to /students/{emailKey} for reference (but not enrolled yet)
            const emailKey = normalizeEmail(email);
            await set(ref(db, `students/${emailKey}`), {
                ...registrationData,
                status: "pending",
                enrolledCourse: null, // Not enrolled until approved
            });

            // Verify the data was written before redirecting
            const verifyRef = ref(db, `students/${emailKey}`);
            const verifySnapshot = await get(verifyRef);
            if (verifySnapshot.exists()) {
                console.log("Student data verified, showing success message");
                setMessage("Registration request submitted successfully! Please wait for admin approval before accessing the course.");
                // Update the state to prevent further form submissions
                setHasExistingRegistration(true);
                setExistingRegistrationData(registrationData);
                // Don't auto-redirect, let user read the message and choose when to go to login
            } else {
                setError("Failed to save registration data. Please try again.");
            }
        } catch (err) {
            setError("An error occurred during registration. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="font-space bg-gray-900 text-white min-h-screen flex flex-col">
            <Navbar />
            <div className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full space-y-8">
                    <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-2xl p-8 shadow-2xl backdrop-blur-sm">
                        <div>
                            <h2 className="mt-6 text-center text-3xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-teal-400 to-sky-300">
                                Complete Your Registration
                            </h2>
                            <p className="mt-2 text-center text-sm text-gray-400">
                                You're signed in. Finish your profile to join a course.
                            </p>
                        </div>

                        {message && (
                            <div className="mt-4 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                                <p className="text-sm text-green-400 text-center mb-4">{message}</p>
                                <div className="text-center">
                                    <button
                                        onClick={() => navigate("/login")}
                                        className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors"
                                    >
                                        Go to Login
                                    </button>
                                </div>
                            </div>
                        )}
                        {error && (
                            <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                                <p className="text-sm text-red-400 text-center">{error}</p>
                            </div>
                        )}

                        {hasExistingRegistration && (
                            <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                                <h3 className="text-lg font-semibold text-yellow-400 mb-2 text-center">
                                    Registration Already Submitted
                                </h3>
                                <div className="text-sm text-yellow-300 space-y-1">
                                    <p><strong>Name:</strong> {existingRegistrationData?.name}</p>
                                    <p><strong>Course:</strong> {existingRegistrationData?.course}</p>
                                    <p><strong>Status:</strong> {existingRegistrationData?.status === "pending" ? "Pending Admin Approval" : existingRegistrationData?.status}</p>
                                </div>
                                <p className="text-xs text-yellow-400 mt-3 text-center">
                                    You have already submitted a registration request. Please wait for admin approval or contact support if you need assistance.
                                </p>
                                <div className="mt-4 text-center">
                                    <button
                                        onClick={() => navigate("/login")}
                                        className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors"
                                    >
                                        Return to Login
                                    </button>
                                </div>
                            </div>
                        )}

                        {!hasExistingRegistration && (
                            <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                                <div className="space-y-4">
                                    <div className="relative">
                                        <label className="block text-sm font-medium text-gray-400 mb-2">
                                            <FaEnvelope className="inline mr-2" />
                                            Email
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={email}
                                            disabled
                                            className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-700 rounded-lg text-gray-400 cursor-not-allowed"
                                        />
                                    </div>

                                    <div className="relative">
                                        <label className="block text-sm font-medium text-gray-400 mb-2">
                                            <FaUser className="inline mr-2" />
                                            Full Name
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition text-white placeholder-gray-500"
                                            placeholder="Enter your full name"
                                            required
                                        />
                                    </div>

                                    <div className="relative">
                                        <label className="block text-sm font-medium text-gray-400 mb-2">
                                            <FaPhone className="inline mr-2" />
                                            WhatsApp Number
                                        </label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition text-white placeholder-gray-500"
                                            placeholder="Enter your number"
                                            required
                                        />
                                    </div>

                                    <div className="relative">
                                        <label className="block text-sm font-medium text-gray-400 mb-2">
                                            <FaUserShield className="inline mr-2" />
                                            Parent's WhatsApp
                                        </label>
                                        <input
                                            type="tel"
                                            name="parentPhone"
                                            value={formData.parentPhone}
                                            onChange={handleChange}
                                            className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition text-white placeholder-gray-500"
                                            placeholder="Enter parent's number"
                                            required
                                        />
                                    </div>

                                    <div className="relative">
                                        <label className="block text-sm font-medium text-gray-400 mb-2">
                                            <FaBook className="inline mr-2" />
                                            Course
                                        </label>
                                        {courses.length === 0 ? (
                                            <div className="text-red-500 text-sm">No courses available. Please contact the administrator.</div>
                                        ) : (
                                            <select
                                                name="course"
                                                value={formData.course}
                                                onChange={handleChange}
                                                className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition text-white"
                                                required
                                            >
                                                <option value="" disabled className="text-white bg-gray-800">Select Course</option>
                                                {courses.map((course) => (
                                                    <option key={course.id} value={course.id} className="text-white bg-gray-800">
                                                        {course.name}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <button
                                        type="submit"
                                        className="w-full px-8 py-3 rounded-full bg-gradient-to-r from-teal-600 to-sky-600 hover:from-teal-500 hover:to-sky-500 text-white font-medium transition-all duration-300 shadow-lg hover:shadow-teal-500/30"
                                        disabled={loading}
                                    >
                                        {loading ? "Submitting..." : "Submit Registration"}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default CompleteRegistration; 